(() => {
  'use strict';
  if (window.UcwCart) return;
  const popup = () => document.querySelector('[data-uw-cart-popup]');
  const route = action => `${window.Shopify?.routes?.root || '/'}cart/${action}.js`;
  let queue = Promise.resolve(), refreshing = Promise.resolve(), previousFocus, scrollY = 0, closeTimer, changing = false;
  const errorMessage = 'De verbinding is onderbroken. Controleer uw winkelwagen voordat u opnieuw probeert.';
  function error(container, message) {
    let target = container.querySelector('[data-ucw-cart-error]');
    if (!target) {
      target = document.createElement('p');
      target.dataset.ucwCartError = '';
      target.className = 'ucw-cart-error';
      target.setAttribute('role', 'alert');
      container.append(target);
    }
    target.textContent = message || '';
    target.hidden = !message;
  }
  function request(action, data) {
    const run = async () => {
      let response;
      const isForm = data instanceof FormData;
      try {
        response = await fetch(route(action), { method: 'POST', headers: { Accept: 'application/json', ...(!isForm && { 'Content-Type': 'application/json' }) }, body: isForm ? data : JSON.stringify(data) });
      } catch { throw new Error(errorMessage); }
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(typeof result?.description === 'string' ? result.description : 'Uw wijziging kon niet worden verwerkt. Controleer uw winkelwagen.');
      return result;
    };
    const result = queue.then(run, run);
    queue = result.catch(() => {});
    return result;
  }
  function open() {
    const root = popup();
    if (!root) return;
    clearTimeout(closeTimer);
    if (root.hidden) {
      previousFocus = document.activeElement;
      scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
    }
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('uw-cart-popup-open');
    requestAnimationFrame(() => {
      root.classList.add('is-open');
      root.querySelector('button[data-uw-cart-close]')?.focus({ preventScroll: true });
    });
  }
  function close() {
    const root = popup();
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('uw-cart-popup-open');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
    previousFocus?.focus({ preventScroll: true });
    closeTimer = setTimeout(() => { if (!root.classList.contains('is-open')) root.hidden = true; }, 220);
  }
  async function updateHeaderCartCount() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Uw winkelwagen kon niet worden geladen.');
    const cart = await response.json();
    document.querySelectorAll('#cart-count, .uw-main-header__cart-count').forEach(element => { element.textContent = cart.item_count; });
    document.querySelectorAll('.uw-main-header__cart').forEach(element => { element.setAttribute('aria-label', `Winkelwagen ${cart.item_count} artikel(en)`); });
  }
  function refresh() {
    const run = async () => {
      const root = popup();
      if (!root) return;
      const url = new URL(location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', root.dataset.sectionId);
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Uw winkelwagen is bijgewerkt, maar kon niet worden getoond. Open de winkelwagenpagina.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-uw-cart-popup]');
      if (!next) throw new Error('Uw winkelwagen kon niet worden vernieuwd. Open de winkelwagenpagina.');
      const content = root.querySelector('.uw-cart-popup__content');
      const position = content?.scrollTop || 0;
      const focused = document.activeElement;
      const focusWasInside = content?.contains(focused) || root.querySelector('.uw-cart-popup__footer')?.contains(focused);
      for (const selector of ['.uw-cart-popup__content', '.uw-cart-popup__footer']) {
        const old = root.querySelector(selector), replacement = next.querySelector(selector);
        if (old && replacement) old.replaceWith(replacement);
        else if (old) old.remove();
        else if (replacement) root.querySelector('.uw-cart-popup__dialog').append(replacement);
      }
      root.querySelector('.uw-cart-popup__content').scrollTop = position;
      if (focusWasInside && !root.hidden) root.querySelector('button[data-uw-cart-close]')?.focus({ preventScroll: true });
      await updateHeaderCartCount();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    };
    refreshing = refreshing.catch(() => {}).then(run);
    return refreshing;
  }
  async function added(title = 'Artikel') {
    try {
      await refresh();
      const root = popup();
      const message = root?.querySelector('[data-uw-success-message]');
      const text = root?.querySelector('[data-uw-success-text]');
      if (message && text) { text.textContent = `${title} is toegevoegd aan uw winkelwagen.`; message.hidden = false; }
      open();
    } catch (reason) {
      open();
      if (popup()) error(popup().querySelector('.uw-cart-popup__content'), reason.message);
    }
  }
  async function changeItem(item, nextTotal) {
    if (changing || !Number.isSafeInteger(nextTotal) || nextTotal < 0) return;
    changing = true;
    const root = popup();
    root.setAttribute('aria-busy', 'true');
    root.querySelectorAll('[data-uw-cart-qty-minus],[data-uw-cart-qty-plus],[data-uw-cart-qty-input],[data-cart-volume-qty]').forEach(control => { control.disabled = true; });
    const paid = (item.dataset.groupPaidKeys || '').split(',').filter(Boolean);
    const free = (item.dataset.groupFreeKeys || '').split(',').filter(Boolean);
    const keys = [...paid, ...free];
    const updates = Object.fromEntries(keys.map(key => [key, 0]));
    if (nextTotal && paid.length) {
      const freeAmount = free.length && item.dataset.groupThreeOne === 'true' ? Math.floor(nextTotal / 4) : 0;
      updates[paid[0]] = nextTotal - freeAmount;
      if (freeAmount) updates[free[0]] = freeAmount;
    } else if (nextTotal && free.length) updates[free[0]] = nextTotal;
    try {
      if (!keys.length) throw new Error('Dit artikel kon niet worden gewijzigd. Vernieuw uw winkelwagen.');
      await request('update', { updates });
      await refresh();
    } catch (reason) {
      error(root.querySelector('.uw-cart-popup__content'), reason.message);
    } finally {
      changing = false;
      root.removeAttribute('aria-busy');
      root.querySelectorAll('[data-uw-cart-qty-minus],[data-uw-cart-qty-plus],[data-uw-cart-qty-input],[data-cart-volume-qty]').forEach(control => { control.disabled = false; });
    }
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-panel="cart"],.uw-main-header__cart,[data-uw-cart-open]');
    if (trigger && popup()) {
      event.preventDefault(); event.stopImmediatePropagation();
      popup().querySelector('[data-uw-success-message]')?.setAttribute('hidden', '');
      open();
      refresh().catch(reason => error(popup().querySelector('.uw-cart-popup__content'), reason.message));
      return;
    }
    if (event.target.closest('[data-uw-cart-close]')) { event.preventDefault(); event.stopImmediatePropagation(); close(); return; }
    const control = event.target.closest('[data-uw-cart-qty-minus],[data-uw-cart-qty-plus],.uw-cart-popup__remove,[data-cart-volume-qty]');
    if (!control || !control.closest('[data-uw-cart-popup]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (changing || control.disabled) return;
    const item = control.closest('.uw-cart-popup__item');
    const input = item?.querySelector('[data-uw-cart-qty-input]');
    if (!item) return;
    let value = 0;
    if (control.hasAttribute('data-cart-volume-qty')) value = Number(control.dataset.cartVolumeQty);
    else if (!control.classList.contains('uw-cart-popup__remove') && input) {
      const step = Number(input.step) || 1;
      value = Math.max(0, Number(input.value) + (control.hasAttribute('data-uw-cart-qty-plus') ? step : -step));
      if (input.max) value = Math.min(Number(input.max), value);
    }
    changeItem(item, value);
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-uw-cart-qty-input]');
    if (!input) return;
    let value = Math.max(0, Math.floor(Number(input.value)));
    if (input.max) value = Math.min(Number(input.max), value);
    changeItem(input.closest('.uw-cart-popup__item'), value);
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.dataset.ucwPdpForm === 'true' || form.hasAttribute('data-ucw-bundle-bridge')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (form.dataset.submitting === 'true' || !form.reportValidity()) return;
    form.dataset.submitting = 'true';
    const button = form.querySelector('[type="submit"]');
    const data = new FormData(form);
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    error(form, '');
    try {
      const result = await request('add', data);
      if (form.closest('[data-uw-main-cart],.uw-related-cart') && document.querySelector('[data-uw-main-cart]')) {
        if (!window.UcwMainCart) throw new Error('Uw artikel is toegevoegd. Vernieuw de winkelwagen om het te bekijken.');
        await window.UcwMainCart.refresh();
      } else await added(result?.product_title || result?.title || 'Artikel');
    } catch (reason) { error(form, reason.message); }
    finally { delete form.dataset.submitting; if (button) { button.disabled = false; button.removeAttribute('aria-busy'); } }
  }, true);
  document.addEventListener('keydown', event => {
    const root = popup();
    if (!root || root.hidden || !root.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const controls = [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')].filter(element => element.getClientRects().length && !element.closest('[hidden]'));
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
  });
  document.addEventListener('ucw:cart-added', event => added(event.detail?.productTitle));
  document.addEventListener('cart:open', open);
  window.UcwCart = Object.freeze({ request, refresh, open, close, added, error });
  window.ucwOpenCartPopup = window.fxlOpenCartPopup = open;
  window.ucwCloseCartPopup = window.fxlCloseCartPopup = close;
  window.ucwUpdateHeaderCartCount = window.fxlUpdateHeaderCartCount = updateHeaderCartCount;
})();

